import React, { useState } from 'react';
import { AcceptanceCriterion } from '../../domain/types';
import { Pill, ProgressBar, Button, ListActionButton } from '../../ui/common';
import { MetricsService } from '../../services/metricsService';

export interface JiraCriteriaSectionProps {
  ticket?: string;
  criteria?: AcceptanceCriterion[];
  onToggle: (ticket: string, index: number) => void;
  onAdd: (ticket: string, text: string) => void;
  onUpdate: (ticket: string, index: number, text: string) => void;
  onDelete: (ticket: string, index: number) => void;
}

export const JiraCriteriaSection: React.FC<JiraCriteriaSectionProps> = ({
  ticket,
  criteria = [],
  onToggle,
  onAdd,
  onUpdate,
  onDelete,
}) => {
  const progress = MetricsService.calculateAcceptanceProgress(criteria);
  const [newCriterionText, setNewCriterionText] = useState<string>('');
  const [editingCriterionIndex, setEditingCriterionIndex] = useState<number | null>(null);
  const [editCriterionText, setEditCriterionText] = useState<string>('');

  const handleAddCriterion = (e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (newCriterionText.trim() && ticket) {
      onAdd(ticket, newCriterionText.trim());
      setNewCriterionText('');
    }
  };

  const startEditCriterion = (index: number, currentText: string) => {
    setEditingCriterionIndex(index);
    setEditCriterionText(currentText);
  };

  const saveEditCriterion = (index: number) => {
    if (editCriterionText.trim() && ticket) {
      onUpdate(ticket, index, editCriterionText.trim());
      setEditingCriterionIndex(null);
      setEditCriterionText('');
    }
  };

  const cancelEditCriterion = () => {
    setEditingCriterionIndex(null);
    setEditCriterionText('');
  };

  return (
    <div
      style={{
        padding: '12px 13px',
        borderRadius: 12,
        background: 'var(--z-plate)',
        border: 'none',
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9, flexWrap: 'wrap' }}
      >
        <span
          style={{
            flex: '1 1 auto',
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--z-text-2)',
            minWidth: 0,
          }}
        >
          Criterios de aceptación
        </span>
        <Pill
          label={`${progress.done} de ${progress.total}`}
          tone={progress.done === progress.total && progress.total > 0 ? 'ok' : 'flat'}
        />
        <span style={{ width: 84, maxWidth: '100%', flex: '1 1 84px' }}>
          <ProgressBar
            progress={progress.pct}
            tone={progress.done === progress.total && progress.total > 0 ? 'ok' : 'accent'}
          />
        </span>
      </div>

      {/* Criteria items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {criteria && criteria.length > 0 ? (
          criteria.map((c, idx) => {
            const state = c.status || c.state || 'open';
            const label = c.text || c.label || '';
            const isEditing = editingCriterionIndex === idx;

            if (isEditing) {
              return (
                <div
                  key={c.id || idx}
                  data-testid={`criterion-edit-${idx}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    padding: '8px 9px',
                    borderRadius: 9,
                    background: 'var(--z-fill)',
                    border: 'none',
                  }}
                >
                  <input
                    data-testid={`criterion-edit-input-${idx}`}
                    value={editCriterionText}
                    onChange={(e) => setEditCriterionText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveEditCriterion(idx);
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelEditCriterion();
                      }
                    }}
                    autoFocus
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      height: 30,
                      padding: '0 9px',
                      borderRadius: 7,
                      border: 'none',
                      background: 'var(--z-surface)',
                      color: 'var(--z-text)',
                      fontSize: 12.5,
                    }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Button variant="primary" size="sm" onClick={() => saveEditCriterion(idx)}>
                      Guardar
                    </Button>
                    <Button variant="ghost" size="sm" onClick={cancelEditCriterion}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={c.id || idx}
                onClick={(e) => {
                  e.stopPropagation();
                  if (ticket) onToggle(ticket, idx);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '8px 9px',
                  borderRadius: 9,
                  cursor: 'pointer',
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    flex: '0 0 17px',
                    width: 17,
                    height: 17,
                    marginTop: 1,
                    borderRadius: 5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    background: state === 'done' ? 'var(--z-ok-bg)' : 'transparent',
                    color: state === 'done' ? 'var(--z-ok-ink)' : 'transparent',
                    border: 'none',
                  }}
                >
                  {state === 'done' ? '✓' : ''}
                </span>
                <span
                  style={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    color: state === 'done' ? 'var(--z-dim)' : 'var(--z-text-3)',
                    textDecoration: state === 'done' ? 'line-through' : 'none',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {label}
                </span>

                <div
                  style={{
                    flex: '0 0 auto',
                    display: 'inline-flex',
                    alignItems: 'flex-start',
                    gap: 4,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ListActionButton
                    kind="edit"
                    aria-label={`Editar criterio ${idx + 1}`}
                    data-testid={`criterion-edit-btn-${idx}`}
                    onClick={() => startEditCriterion(idx, label)}
                  />
                  <ListActionButton
                    kind="delete"
                    aria-label={`Eliminar criterio ${idx + 1}`}
                    data-testid={`criterion-delete-btn-${idx}`}
                    onClick={() => ticket && onDelete(ticket, idx)}
                  />
                </div>
              </div>
            );
          })
        ) : (
          <div style={{ fontSize: 12, color: 'var(--z-dim)', padding: '6px 4px' }}>
            No hay criterios de aceptación registrados.
          </div>
        )}
      </div>

      {/* Add new criterion input form */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 10,
          paddingTop: 8,
          borderTop: 'none',
        }}
      >
        <input
          data-testid="add-criterion-input"
          placeholder="Añadir criterio de aceptación…"
          value={newCriterionText}
          onChange={(e) => setNewCriterionText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddCriterion();
            }
          }}
          style={{
            flex: '1 1 auto',
            height: 30,
            padding: '0 10px',
            borderRadius: 7,
            border: 'none',
            background: 'var(--z-fill)',
            color: 'var(--z-text)',
            fontSize: 12,
            minWidth: 0,
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleAddCriterion}
          disabled={!newCriterionText.trim()}
          style={{ flexShrink: 0 }}
        >
          + Añadir
        </Button>
      </div>
    </div>
  );
};
